import React, { useState } from 'react';
import Button from '../../common/Button';
import VoiceRecorder from './VoiceRecorder';
import { useAuth } from '../../../contexts/AuthContext';
import { uploadJobSeekerVoiceRecording } from '../../../lib/supabase/jobSeekers';
import { JOB_TYPES, EXPERIENCE_LEVELS, AVAILABILITY_OPTIONS } from '../../../types/profiles';
import type { JobSeekerProfileData } from '../../../types/profiles';

interface JobSeekerFormProps {
  initialData: JobSeekerProfileData;
  onSubmit: (data: JobSeekerProfileData) => void;
  onCancel?: () => void;
}

export default function JobSeekerForm({ initialData, onSubmit, onCancel }: JobSeekerFormProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<JobSeekerProfileData>(initialData);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedAudioFile, setSelectedAudioFile] = useState<File | null>(null);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [voiceUploadSuccess, setVoiceUploadSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      setError('Please sign in to create a profile');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const cleanData = { ...formData };

      if (!cleanData.photoUrl?.trim()) {
        delete cleanData.photoUrl;
      }

      if (selectedFile) {
        cleanData.resumeFile = selectedFile;
      }

      if (selectedAudioFile) {
        cleanData.audioFile = selectedAudioFile;
      }

      if (selectedPhotoFile) {
        cleanData.photoFile = selectedPhotoFile;
      }

      if (selectedVideoFile) {
        cleanData.videoFile = selectedVideoFile;
      }

      onSubmit(cleanData);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Job Types
          </label>
          <select
            multiple
            value={formData.jobTypes}
            onChange={e => setFormData(prev => ({
              ...prev,
              jobTypes: Array.from(e.target.selectedOptions, option => option.value)
            }))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            size={4}
            required
          >
            {JOB_TYPES.map(type => (
              <option key={type} value={type} className="py-1">
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Experience Level
          </label>
          <select
            value={formData.experience}
            onChange={e => setFormData(prev => ({ ...prev, experience: e.target.value }))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            required
          >
            <option value="">Select Experience</option>
            {EXPERIENCE_LEVELS.map(level => (
              <option key={level} value={level}>
                {level === 'entry' ? 'Entry Level' : `${level} years`}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Availability
          </label>
          <select
            value={formData.availability}
            onChange={e => setFormData(prev => ({ ...prev, availability: e.target.value }))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            required
          >
            {AVAILABILITY_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option === 'immediate' ? 'Available Immediately' : 
                 option === '2-weeks' ? 'Available in 2 Weeks' :
                 option === '1-month' ? 'Available in 1 Month' : 
                 'Flexible Availability'}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Desired Pay ($/hour)
          </label>
          <input
            type="number"
            value={formData.desiredPay}
            onChange={e => setFormData(prev => ({ ...prev, desiredPay: e.target.value }))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            min="0"
            step="0.50"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Skills (comma-separated)
          </label>
          <input
            type="text"
            value={formData.skills.join(', ')}
            onChange={e => setFormData(prev => ({
              ...prev,
              skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
            }))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="e.g., Animal Care, Fencing, Equipment Operation"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Preferred Locations (comma-separated)
          </label>
          <input
            type="text"
            value={formData.locations.join(', ')}
            onChange={e => setFormData(prev => ({
              ...prev,
              locations: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
            }))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="e.g., Texas, Montana, Any Location"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            About
          </label>
          <textarea
            value={formData.about}
            onChange={e => setFormData(prev => ({ ...prev, about: e.target.value }))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            rows={3}
            placeholder="Brief description about yourself and your experience..."
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Photo Upload <span className="text-gray-500">(optional)</span>
          </label>
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                if (file.size > 5 * 1024 * 1024) {
                  setError('Photo must be less than 5MB');
                  e.target.value = '';
                  return;
                }
                setSelectedPhotoFile(file);
                setError(null);
              }
            }}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Upload a photo (max 5MB, JPG, PNG, GIF, or WebP format)
          </p>
          {selectedPhotoFile && (
            <p className="mt-1 text-xs text-green-600">
              Selected: {selectedPhotoFile.name} ({(selectedPhotoFile.size / 1024 / 1024).toFixed(2)}MB)
            </p>
          )}
          {formData.photoUrl && !selectedPhotoFile && (
            <p className="mt-1 text-xs text-blue-600">
              Current photo uploaded
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            type="email"
            value={formData.email || ''}
            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="your.email@example.com"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number
          </label>
          <input
            type="tel"
            value={formData.phone || ''}
            onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="(555) 123-4567"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Resume <span className="text-gray-500">(optional)</span>
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setSelectedFile(file);
            }}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Upload your resume (PDF, DOC, or DOCX format)
          </p>
          {selectedFile && (
            <p className="mt-1 text-xs text-green-600">
              Selected: {selectedFile.name}
            </p>
          )}
          {formData.resumeUrl && !selectedFile && (
            <p className="mt-1 text-xs text-blue-600">
              Current resume uploaded
            </p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Voice Introduction <span className="text-gray-500">(optional)</span>
          </label>

          {formData.voiceIntroUrl && !showVoiceRecorder ? (
            <div className="space-y-2">
              <audio controls className="w-full">
                <source src={formData.voiceIntroUrl} type="audio/webm" />
                Your browser does not support the audio element.
              </audio>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowVoiceRecorder(true)}
                >
                  Record New Voice Introduction
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(formData.voiceIntroUrl!);
                    setVoiceUploadSuccess(true);
                    setTimeout(() => setVoiceUploadSuccess(false), 2000);
                  }}
                >
                  🔗 Copy Link
                </Button>
              </div>
              {voiceUploadSuccess && (
                <p className="text-xs text-green-600">✓ Link copied to clipboard!</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <VoiceRecorder
                maxDuration={60}
                onSave={async (audioBlob) => {
                  if (!user?.id) throw new Error('User not authenticated');
                  const { url, error: uploadError } = await uploadJobSeekerVoiceRecording(audioBlob, user.id);
                  if (uploadError) throw uploadError;
                  if (url) {
                    setFormData(prev => ({ ...prev, voiceIntroUrl: url }));
                    setShowVoiceRecorder(false);
                    setVoiceUploadSuccess(true);
                    setTimeout(() => setVoiceUploadSuccess(false), 5000);
                  }
                }}
              />
              {voiceUploadSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
                  ✓ Voice recording uploaded successfully! Public URL has been generated.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Video Introduction <span className="text-gray-500">(optional)</span>
          </label>
          <input
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const maxSize = 50 * 1024 * 1024;
                if (file.size > maxSize) {
                  setError('Video file must be less than 50MB');
                  e.target.value = '';
                  return;
                }
                setSelectedVideoFile(file);
                setError(null);
              }
            }}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Upload a short video introduction (max 1 minute, 50MB max)
          </p>
          {selectedVideoFile && (
            <p className="mt-1 text-xs text-green-600">
              Selected: {selectedVideoFile.name} ({(selectedVideoFile.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          )}
          {formData.videoUrl && !selectedVideoFile && (
            <div className="mt-2">
              <video
                controls
                className="w-full max-w-md rounded-lg shadow-sm"
              >
                <source src={formData.videoUrl} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <p className="mt-1 text-xs text-blue-600">
                Current video uploaded
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={loading}
        >
          Save Profile
        </Button>
      </div>
    </form>
  );
}